import React from 'react';
import { StockQuote } from '../services/mcpClient';

interface StockQuoteCardProps {
  quote: StockQuote;
}

const StockQuoteCard: React.FC<StockQuoteCardProps> = ({ quote }) => {
  const formatPrice = (price: number | null): string => {
    if (price === null) return 'N/A';
    return `$${price.toFixed(2)}`;
  };

  const formatChange = (change: number | null, changePercent: number | null): string => {
    if (change === null || changePercent === null) return 'N/A';
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(2)} (${sign}${(changePercent * 100).toFixed(2)}%)`;
  };

  const formatVolume = (volume: number | null): string => {
    if (volume === null) return 'N/A';
    if (volume >= 1e9) return `${(volume / 1e9).toFixed(2)}B`;
    if (volume >= 1e6) return `${(volume / 1e6).toFixed(2)}M`;
    if (volume >= 1e3) return `${(volume / 1e3).toFixed(2)}K`;
    return volume.toString();
  };

  const formatMarketCap = (marketCap: number | null): string => {
    if (marketCap === null) return 'N/A';
    if (marketCap >= 1e12) return `$${(marketCap / 1e12).toFixed(2)}T`;
    if (marketCap >= 1e9) return `$${(marketCap / 1e9).toFixed(2)}B`;
    if (marketCap >= 1e6) return `$${(marketCap / 1e6).toFixed(2)}M`;
    return `$${marketCap.toFixed(2)}`;
  };

  const getChangeColor = (change: number | null): string => {
    if (change === null) return '#666';
    return change >= 0 ? '#10B981' : '#EF4444';
  };

  const getMarketStateColor = (marketState: string): string => {
    switch (marketState.toUpperCase()) {
      case 'REGULAR':
        return '#10B981';
      case 'PRE':
      case 'PREPRE':
        return '#F59E0B';
      case 'POST':
      case 'POSTPOST':
        return '#8B5CF6';
      case 'CLOSED':
        return '#6B7280';
      default:
        return '#6B7280';
    }
  };

  return (
    <div className="stock-card">
      <div className="stock-header">
        <div className="stock-symbol">
          <h3>{quote.symbol}</h3>
          <span className="market-state" style={{ color: getMarketStateColor(quote.marketState) }}>
            {quote.marketState}
          </span>
        </div>
        <div className="stock-price">
          <span className="price">{formatPrice(quote.price)}</span>
          <span 
            className="change" 
            style={{ color: getChangeColor(quote.change) }}
          >
            {formatChange(quote.change, quote.changePercent)}
          </span>
        </div>
      </div>
      
      <div className="stock-info">
        <h4 className="stock-name">{quote.name}</h4>
        <div className="stock-details">
          <div className="detail-row">
            <span className="label">Previous Close:</span>
            <span className="value">{formatPrice(quote.previousClose)}</span>
          </div>
          <div className="detail-row">
            <span className="label">Day Range:</span>
            <span className="value">
              {formatPrice(quote.dayLow)} - {formatPrice(quote.dayHigh)}
            </span>
          </div>
          <div className="detail-row">
            <span className="label">Volume:</span>
            <span className="value">{formatVolume(quote.volume)}</span>
          </div>
          <div className="detail-row">
            <span className="label">Market Cap:</span>
            <span className="value">{formatMarketCap(quote.marketCap)}</span>
          </div>
          <div className="detail-row">
            <span className="label">52W Range:</span>
            <span className="value">
              {formatPrice(quote.fiftyTwoWeekLow)} - {formatPrice(quote.fiftyTwoWeekHigh)}
            </span>
          </div>
          <div className="detail-row">
            <span className="label">Exchange:</span>
            <span className="value">{quote.exchange}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockQuoteCard;
